import React from "react";

import {
  ACTIVITY_SOURCE_ONLYDASH,
  ActivityFilterOptions,
  ActivityFilters,
  activityActorLabel,
  activityFiltersAreSet,
} from "../lib/activity-filters";
import { Button } from "./ui/Button";
import { Input, inputClassName } from "./ui/Input";

const fieldClass = inputClassName("sm");

const labelClass = "flex min-w-[9rem] flex-1 flex-col gap-1 text-xs text-od-muted";

type Props = {
  filters: ActivityFilters;
  options: ActivityFilterOptions;
  exporting?: boolean;
  onChange: (next: ActivityFilters) => void;
  onClear: () => void;
  onExport: () => void;
};

/**
 * Filter controls above the Activity list (MOSA-879).
 *
 * Person and Tab options come from GET /audit/:id/activity/filters.
 * Dates stay as local calendar days in the URL; the API call converts them.
 */
export const ActivityFilterBar: React.FC<Props> = ({
  filters,
  options,
  exporting = false,
  onChange,
  onClear,
  onExport,
}) => {
  const filtersSet = activityFiltersAreSet(filters);

  const patch = (part: Partial<ActivityFilters>) => {
    const next: ActivityFilters = { ...filters, ...part };
    // Clear keys that the control emptied, so the URL drops them.
    (Object.keys(part) as (keyof ActivityFilters)[]).forEach((key) => {
      if (!part[key]) delete next[key];
    });
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="activity-filters">
      <div className="flex flex-wrap items-end gap-3">
        <label className={labelClass}>
          <span>Person</span>
          <select
            className={fieldClass}
            value={filters.actor ?? ""}
            onChange={(event) => patch({ actor: event.target.value || undefined })}
            aria-label="Person"
          >
            <option value="">Any person</option>
            {filters.actor &&
              !options.actors.some((actor) => actor.key === filters.actor) && (
                <option value={filters.actor}>{filters.actor}</option>
              )}
            {options.actors.map((actor) => (
              <option key={actor.key} value={actor.key}>
                {activityActorLabel(actor)}
              </option>
            ))}
          </select>
        </label>

        <label className={labelClass}>
          <span>Tab</span>
          <select
            className={fieldClass}
            value={filters.tab ?? ""}
            onChange={(event) => patch({ tab: event.target.value || undefined })}
            aria-label="Tab"
          >
            <option value="">Any tab</option>
            {filters.tab && !options.tabs.includes(filters.tab) && (
              <option value={filters.tab}>{filters.tab}</option>
            )}
            {options.tabs.map((tab) => (
              <option key={tab} value={tab}>
                {tab}
              </option>
            ))}
          </select>
        </label>

        <label className={labelClass}>
          <span>From</span>
          <Input
            type="date"
            size="sm"
            value={filters.from ?? ""}
            onChange={(event) => patch({ from: event.target.value || undefined })}
            aria-label="From date"
          />
        </label>

        <label className={labelClass}>
          <span>To</span>
          <Input
            type="date"
            size="sm"
            value={filters.to ?? ""}
            onChange={(event) => patch({ to: event.target.value || undefined })}
            aria-label="To date"
          />
        </label>

        <label className={labelClass}>
          <span>Source</span>
          <select
            className={fieldClass}
            value={filters.source ?? ""}
            onChange={(event) =>
              patch({
                source:
                  event.target.value === ACTIVITY_SOURCE_ONLYDASH
                    ? ACTIVITY_SOURCE_ONLYDASH
                    : undefined,
              })
            }
            aria-label="Source"
          >
            <option value="">Any source</option>
            <option value={ACTIVITY_SOURCE_ONLYDASH}>OnlyDash</option>
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={exporting}
          onClick={onExport}
        >
          Export CSV
        </Button>
        {filtersSet && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
};
